import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { z } from 'zod';

import { FormField } from '@/components/shared/form-field';
import { EmptyState } from '@/components/shared/states';
import { Button } from '@/components/ui/button';
import { PasswordInput } from '@/components/ui/password-input';
import { useResetPassword } from '@/hooks/use-auth';

import { AuthLayout } from './auth-layout';
import { FormError } from './form-error';
import { messageFor } from './validation-message';

const schema = z
  .object({
    password: z.string().min(12, 'passwordTooShort'),
    confirmPassword: z.string(),
  })
  .refine((values) => values.password === values.confirmPassword, {
    // A mistyped new password locks someone out of an account they were in the middle
    // of recovering, so it is confirmed here rather than discovered at the next sign-in.
    message: 'passwordMismatch',
    path: ['confirmPassword'],
  });

type Values = z.infer<typeof schema>;

export const ResetPasswordPage = () => {
  const { t } = useTranslation('auth');
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token');
  const resetPassword = useResetPassword();

  const form = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: { password: '', confirmPassword: '' },
  });

  const submit = form.handleSubmit(async (values) => {
    if (!token) return;
    try {
      await resetPassword.mutateAsync({ token, password: values.password });
      void navigate('/', { replace: true });
    } catch {
      // Shown by the mutation's error state.
    }
  });

  if (!token) {
    return (
      <AuthLayout title={t('resetPassword.title')}>
        <EmptyState
          title={t('resetPassword.title')}
          body={t('resetPassword.missingToken')}
          action={
            <Button asChild>
              <Link to="/forgot-password">{t('resetPassword.requestNew')}</Link>
            </Button>
          }
        />
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title={t('resetPassword.title')} description={t('resetPassword.description')}>
      <form onSubmit={(event) => void submit(event)} className="space-y-4" noValidate>
        <FormField
          label={t('field.newPassword')}
          required
          tipId="auth.password"
          error={messageFor(form.formState.errors.password?.message, t)}
        >
          {(field) => (
            <PasswordInput
              {...field}
              {...form.register('password')}
              autoComplete="new-password"
              autoFocus
            />
          )}
        </FormField>

        <FormField
          label={t('field.confirmPassword')}
          required
          error={messageFor(form.formState.errors.confirmPassword?.message, t)}
        >
          {(field) => (
            <PasswordInput
              {...field}
              {...form.register('confirmPassword')}
              autoComplete="new-password"
            />
          )}
        </FormField>

        <FormError error={resetPassword.error} />

        <div className="flex items-center justify-between gap-2">
          <Link to="/forgot-password" className="text-body-sm text-brand hover:underline">
            {t('resetPassword.requestNew')}
          </Link>
          <Button type="submit" loading={form.formState.isSubmitting}>
            {t('resetPassword.submit')}
          </Button>
        </div>
      </form>
    </AuthLayout>
  );
};
